import {
  StorageOptions,
  StorageStatus,
  StorageError,
  StorageErrorCode,
} from "./types";
import { BaseStorage } from "./base-storage";
import { logger } from "../../utils/logger";

/**
 * localStorage implementation of StorageLayer
 */
export class LocalStorageManager extends BaseStorage {
  private readonly DEFAULT_QUOTA = 5 * 1024 * 1024; // 5MB default quota

  constructor() {
    super("localStorage");
  }

  /**
   * Get storage status including availability and space usage
   */
  async getStatus(): Promise<StorageStatus> {
    try {
      const available = this.isStorageAvailable();
      const usedSpace = this.calculateUsedSpace();
      const totalSpace = this.DEFAULT_QUOTA;

      // Get operation metrics
      const metrics = this.getOperationMetrics();

      return {
        available,
        usedSpace,
        totalSpace,
        operations: metrics.operations,
        health: metrics.health,
      };
    } catch (error) {
      logger.error("Failed to get storage status", {
        error: error instanceof Error ? error : new Error(String(error)),
      });

      return {
        available: false,
        usedSpace: 0,
        totalSpace: 0,
        errorMessage: "Failed to get storage status",
      };
    }
  }

  /**
   * Implement storage-specific save operation
   */
  protected async performSave<T>(
    key: string,
    data: T,
    _options?: StorageOptions,
  ): Promise<void> {
    try {
      // Check space availability
      const serialized = JSON.stringify(data);
      const requiredSpace = serialized.length * 2; // Unicode characters

      const status = await this.getStatus();
      if (status.usedSpace + requiredSpace > status.totalSpace) {
        throw new StorageError(
          "Insufficient storage space",
          StorageErrorCode.QUOTA_EXCEEDED,
          {
            required: requiredSpace,
            available: status.totalSpace - status.usedSpace,
          },
        );
      }

      // Perform save
      localStorage.setItem(key, serialized);
    } catch (error) {
      if (error instanceof StorageError) {
        throw error;
      }

      // Handle localStorage-specific errors
      if (error instanceof Error) {
        if (error.name === "QuotaExceededError") {
          throw new StorageError(
            "Storage quota exceeded",
            StorageErrorCode.QUOTA_EXCEEDED,
          );
        }
      }

      throw new StorageError(
        "Failed to save data",
        StorageErrorCode.OPERATION_FAILED,
        { originalError: error },
      );
    }
  }

  /**
   * Implement storage-specific load operation
   */
  protected async performLoad<T>(
    key: string,
    _options?: StorageOptions,
  ): Promise<T> {
    try {
      const data = localStorage.getItem(key);
      if (data === null) {
        throw new StorageError(
          "Data not found",
          StorageErrorCode.NOT_AVAILABLE,
        );
      }

      return JSON.parse(data) as T;
    } catch (error) {
      if (error instanceof StorageError) {
        throw error;
      }

      // Handle JSON parse errors
      if (error instanceof SyntaxError) {
        throw new StorageError(
          "Corrupted data",
          StorageErrorCode.CORRUPTED_DATA,
          { originalError: error },
        );
      }

      throw new StorageError(
        "Failed to load data",
        StorageErrorCode.OPERATION_FAILED,
        { originalError: error },
      );
    }
  }

  /**
   * Implement storage-specific delete operation
   */
  protected async performDelete(
    key: string,
    _options?: StorageOptions,
  ): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      throw new StorageError(
        "Failed to delete data",
        StorageErrorCode.OPERATION_FAILED,
        { originalError: error },
      );
    }
  }

  /**
   * Implement storage-specific clear operation
   */
  protected async performClear(_options?: StorageOptions): Promise<void> {
    try {
      localStorage.clear();
    } catch (error) {
      throw new StorageError(
        "Failed to clear storage",
        StorageErrorCode.OPERATION_FAILED,
        { originalError: error },
      );
    }
  }

  /**
   * Check if localStorage is available
   */
  private isStorageAvailable(): boolean {
    try {
      const test = "__storage_test__";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Calculate used storage space in bytes
   */
  private calculateUsedSpace(): number {
    let total = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const item = localStorage.getItem(key);
        if (item) {
          total += item.length * 2; // Unicode characters
        }
      }
    }
    return total;
  }

  /**
   * Get operation metrics
   */
  private getOperationMetrics(): {
    operations: { pending: number; completed: number; failed: number };
    health: { errorRate: number; avgOperationTime: number };
  } {
    // In localStorage, operations are synchronous
    return {
      operations: {
        pending: 0,
        completed: localStorage.length,
        failed: 0,
      },
      health: {
        errorRate: 0,
        avgOperationTime: 0, // Synchronous operations
      },
    };
  }
}

// Export singleton instance
export const localStorageManager = new LocalStorageManager();
